// Xenit Design Theme
// Based on official brand sheet (colors, typography, visual identity)

export const colors = {
  // Brand palette
  yellow: "#FEF5CA",
  green: "#02D35F",
  teal: "#00C081",
  turquoise: "#02A49C",
  lightBlue: "#71D6E0",
  darkBlue: "#0D2C40",
  pink: "#F88DA7",
  offWhite: "#F4F3EF",

  // Extended palette
  white: "#FFFFFF",
  black: "#000000",
  mediumGray: "#75787B",
  lightGray: "#D0D3D4",
  darkGreen: "#009530",

  // Semantic aliases
  primary: "#02D35F",
  primaryButton: "#0D2C40",
  primaryHover: "#01B04F",
  primaryLight: "rgba(2, 211, 95, 0.1)",
  secondary: "#0D2C40",
  accent: "#02A49C",
  background: "#F4F3EF",

  // Status
  success: "#02D35F",
  error: "#d32f2f",
  warning: "#ff9800",
  info: "#71D6E0",
} as const;

export type ColorKey = keyof typeof colors;

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

export const radius = {
  sm: 6,
  md: 8,
  lg: 12,
};

export const fonts = {
  // From brand sheet: Maison Neue Demi/Book + Maison Neue Mono
  main: `"Maison Neue", "Inter", "Source Sans Pro", system-ui, sans-serif`,
  mono: `"Maison Neue Mono", "JetBrains Mono", monospace`,
};

// Reusable button style presets following brand guidelines
export const buttonStyles = {
  primary: {
    backgroundColor: colors.green,
    color: colors.white,
    borderRadius: `${radius.md}px`,
    fontFamily: fonts.main,
    fontWeight: 600,
    textTransform: "none" as const,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: colors.teal,
      transform: "scale(1.02)",
    },
  },
  secondary: {
    backgroundColor: "transparent",
    color: colors.darkBlue,
    border: `1.5px solid ${colors.darkBlue}`,
    borderRadius: `${radius.md}px`,
    fontFamily: fonts.main,
    fontWeight: 600,
    textTransform: "none" as const,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: colors.offWhite,
      borderColor: colors.turquoise,
      color: colors.turquoise,
      transform: "scale(1.02)",
    },
  },
  accent: {
    backgroundColor: colors.teal,
    color: colors.white,
    borderRadius: `${radius.md}px`,
    fontFamily: fonts.main,
    fontWeight: 600,
    textTransform: "none" as const,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: colors.turquoise,
      transform: "scale(1.02)",
    },
  },
};

// Reusable card style preset
export const cardStyles = {
  default: {
    borderRadius: `${radius.lg}px`,
    backgroundColor: colors.white,
    border: `1px solid ${colors.lightGray}`,
    boxShadow: "0 1px 4px rgba(13, 44, 64, 0.06)",
  },
  elevated: {
    borderRadius: `${radius.lg}px`,
    backgroundColor: colors.white,
    boxShadow: "0 2px 12px rgba(13, 44, 64, 0.08)",
  },
  highlight: {
    borderRadius: `${radius.lg}px`,
    backgroundColor: colors.yellow,
    border: `1px solid rgba(2, 211, 95, 0.2)`,
  },
};

// Table header style
export const tableHeaderStyle = {
  fontWeight: 600,
  backgroundColor: colors.offWhite,
  color: colors.darkBlue,
  fontFamily: fonts.main,
  fontSize: "0.8rem",
  letterSpacing: "0.02em",
  borderBottom: `2px solid ${colors.lightGray}`,
};

export const theme = {
  colors,
  spacing,
  radius,
  fonts,
  buttonStyles,
  cardStyles,
  tableHeaderStyle,
};

export default theme;
